<script setup lang="ts">
import { ref,reactive,onMounted,onBeforeUnmount,toRefs,watch } from 'vue'
import CustomList from "../../../../compositions/CustomList.vue";
import ModalDialog from "../../../../compositions/ModalDialog.vue";
import SearchBox from "../../../../compositions/SearchBox.vue";
import SelectBox from "../../../../compositions/SelectBox.vue";
import Chart from "../../../../compositions/Chart.vue";
import SummaryMedicineComponent from '../../../Common/SummaryMedicineComponent.vue';
import config from "../../../../config.js";
import { errorAlert } from "../../../../plugins/alert";
import { getFieldValue } from "../../../../plugins/utility";
import qs from 'qs';
import axios from '../../../../plugins/axios.ts';
import { deepCopy } from "../../../../plugins/utility";
import HelpAction from '../../../../compositions/HelpAction.vue';


const props = defineProps({
    apis:{
      type:Object,
      required:true,
    },
    identityId: {
      typ:Number,
      default:() => {
        return 0;
      }
    },
    searchParams: {
      typ:Object,
      default:() => {
        return {};
      }
    },
    isVisible:{
        type:Boolean,
        default: () => {
            return false;
        }
    }
})

const { isVisible,identityId,searchParams } = toRefs(props);
const listRef = ref();
const searchParamsData = reactive(props.searchParams);
const medicineSummaryFields = reactive([
  {field_name: '薬局名', field_key: 'pharmacy_name', width: '10%', align: 'text-left',sorted:true},
  {field_name: '採用品', field_key: 'product_name', width: '15%', align: 'text-left',sorted:true},
  {field_name: '分類', field_key: 'warning_mark', width: '5%', align: 'text-left',sorted:true},
  {field_name: '最新<br>薬価', field_key: 'medicine_unit_price', width: '5%', align: 'text-center',sorted:true},
  {field_name: '最新<br>購入額', field_key: 'current_unit_price', width: '5%', align: 'text-center',sorted:true},
  {field_name: '棚卸差分数量', field_key: 'stock_number', width: '5%', align: 'text-center',sorted:true},
  {field_name: '薬価在庫金額', field_key: 'stock_price_org', width: '5%', align: 'text-center',sorted:true},
  {field_name: '在庫金額(税込)', field_key: 'stock_price', width: '5%', align: 'text-center',sorted:true},
]);
const summaryData = reactive([]);
const summaryChartHistoryData = reactive([]);
const summaryHistoryType = ref(0);
const summaryHistoryOptions = reactive([
  {id:0,name:'年単位'},
  {id:1,name:'月単位'},
]);
const onSummaryHistoryTypeChange = (value,label) => {
  summaryHistoryType.value = value;
  getSummaryHistory();
}
const getSummary = async () => {
  let params = deepCopy(searchParamsData);
  const paramsSerializer = (params) => qs.stringify(params,{ arrayFormat: 'repeat'});
  let response = await axios.get(props.apis.summary_stock_summary,{params:params,paramsSerializer});
  summaryData.splice(0);
  summaryData.push(response.data.summary);
}
const getSummaryHistory = async () => {
  let params = deepCopy(searchParamsData);
  params['history_type'] = summaryHistoryType.value;
  const paramsSerializer = (params) => qs.stringify(params,{ arrayFormat: 'repeat'});
  let response = await axios.get(props.apis.summary_stock_control_summary_list,{params:params,paramsSerializer});
  let chartRecord = {};
  chartRecord.labels = [];
  for (let i=0;i<response.data.list.length;i++) {
    chartRecord.labels.push(response.data.list[i].summary_key);
  }
  chartRecord.datasets = [];
  let inventory_number = [];
  for (let i=0;i<response.data.list.length;i++) {
    inventory_number.push(response.data.list[i].inventory_number);
  }
  chartRecord.datasets.push({
      type:'bar',
      label:'棚卸差分数量',
      backgroundColor:'#2dc181',
      data:inventory_number,
  });

  summaryChartHistoryData.splice(0);
  summaryChartHistoryData.push(chartRecord);
}

const refreshView = (force=false) => {
  if (isVisible.value == false) {
    return;
  }
  if (typeof listRef.value !== 'undefined') {
    listRef.value.resetItem();
    getSummary();
    getSummaryHistory();
  }
}
watch(isVisible,(newValue, oldValue) => {
  if (newValue) {
    refreshView();
  }
})
watch(identityId,(newValue, oldValue) => {
  refreshView();
})
watch(searchParams,(newValue, oldValue) => {
  refreshView();
},{deep:true})
onMounted(() => {
  refreshView();
});
</script>

<style scoped>
.summary-header {
  min-height:54px;
}
.summary-chart {
  min-height:342px;
}
</style>
<template>
<div v-show="isVisible" class="relative">
  <div class="absolute top-2 left-2" style="z-index:39;">
    <help-action
      help-key="dashboard_stock_control_help"
    ></help-action>
  </div>
  <div class="pl-8 w-full border flex items-center justify-between px-4 summary-header" v-for="(summaryRecord,index) in summaryData" :key="index">
    <div class="text-center">
      <div><span class="border-b text-sm">棚卸差分金額(税込み)</span></div>
      <div class="text-bold text-lg">\{{ summaryRecord.stock_control_stock_price.toLocaleString() }}</div>
    </div>
    <div class="text-center">
      <div><span class="border-b text-sm">棚卸差分品目数</span></div>
      <div class="text-bold text-lg">{{ summaryRecord.stock_control_stock_count.toLocaleString() }}</div>
    </div>
  </div>
  <div class="w-full mt-1 flex flex-col summary-chart">
    <div v-for="(summaryChartHistoryRecord,index) in summaryChartHistoryData" :key="index">
      <div class="font-bold">【時系列(棚卸差分数量)】</div>
      <div class="w-1/5">
        <select-box
          :value="summaryHistoryType"
          :options="summaryHistoryOptions"
          :on-change="onSummaryHistoryTypeChange"
        ></select-box>
      </div>
      <div class="w-full" >
        <chart
          chart-type="bar"
          height="280px"
          :chart-data="summaryChartHistoryRecord"
        ></chart>
      </div>
    </div>
  </div>
  <div class="w-full mt-1">
    <div class="font-bold">【棚卸差分一覧】</div>
    <custom-list
      ref="listRef"
      :api-url="apis.summary_stock_control_list"
      :table-fields="medicineSummaryFields"
      :option-fields="searchParamsData"
      :is-downloadable="true"
      font-size="0.8em"
      height="40vh"
    >
    <template v-slot:item="slotProps">
      <summary-medicine-component
        :apis="apis"
        :item="slotProps.item"
        :fields="medicineSummaryFields"
      ></summary-medicine-component>
    </template>
    </custom-list>
  </div>
</div>
</template>
